import { useCallback, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from '../../store';
import { removeDocumentContext } from '@repancy/core';
import { testIds } from '../../lib/testIds';

interface DocumentContextProps {
  sessionId: string;
}

const EMPTY_IDS: readonly string[] = Object.freeze([]);

const containerStyle: React.CSSProperties = {
  borderStyle: 'solid',
  borderWidth: 2,
  background: '#fff',
  padding: 6,
  marginBottom: 8,
  minHeight: 60,
};

const labelStyle: React.CSSProperties = {
  fontFamily: "var(--xp-font-body)",
  fontSize: 12,
  fontWeight: 'bold',
  marginBottom: 4,
  display: 'block',
  color: '#000',
};

const docCardStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  padding: '3px 6px',
  borderStyle: 'solid',
  background: '#ece9d8',
  marginBottom: 3,
  fontFamily: "var(--xp-font-body)",
  fontSize: 11,
};

const removeBtnStyle: React.CSSProperties = {
  background: '#ece9d8',
  border: '1px outset #ece9d8',
  cursor: 'pointer',
  fontSize: 10,
  fontWeight: 'bold',
  padding: '0 4px',
  color: '#800000',
  lineHeight: 1,
};

const badgeStyle: React.CSSProperties = {
  fontSize: 9,
  padding: '1px 4px',
  borderRadius: 2,
  fontWeight: 'bold',
  letterSpacing: 0.3,
};

const CONF_COLORS: Record<string, { bg: string; color: string }> = {
  Public: { bg: '#008000', color: '#fff' },
  Internal: { bg: '#808000', color: '#fff' },
  Confidential: { bg: '#800000', color: '#fff' },
  'Highly Confidential': { bg: '#ff0000', color: '#fff' },
  'Attorney-Client Privileged': { bg: 'var(--xp-selection)', color: '#fff' },
};

const dropZoneStyle: React.CSSProperties = {
  border: '2px dashed #808080',
  padding: 12,
  textAlign: 'center',
  color: '#808080',
  fontFamily: "var(--xp-font-body)",
  fontSize: 11,
  fontStyle: 'italic',
  marginTop: 4,
};

export function DocumentContext({ sessionId }: DocumentContextProps) {
  const dispatch = useAppDispatch();
  const documentIds = useAppSelector(
    (s) => s.promptSessions.sessions.entities[sessionId]?.documentContextIds ?? EMPTY_IDS,
  );
  const documentEntities = useAppSelector((s) => s.documents.entities);

  const handleRemove = useCallback(
    (documentId: string) => {
      dispatch(removeDocumentContext({ sessionId, documentId: documentId as any }));
    },
    [dispatch, sessionId],
  );

  const attachedDocs = useMemo(
    () => documentIds.map((id) => documentEntities[id]).filter(Boolean),
    [documentIds, documentEntities],
  );

  return (
    <div style={{ marginBottom: 8 }}>
      <label style={labelStyle}>Document Context</label>
      <div style={containerStyle} data-testid={testIds.promptLab.docContext}>
        {attachedDocs.length === 0 && documentIds.length === 0 && (
          <div
            style={{
              color: '#808080',
              fontStyle: 'italic',
              fontSize: 11,
              fontFamily: "var(--xp-font-body)",
              textAlign: 'center',
              padding: 8,
            }}
          >
            No documents attached
          </div>
        )}

        {attachedDocs.map((doc) => {
          if (!doc) return null;
          const confColors = CONF_COLORS[doc.confidentialityLevel] ?? { bg: '#808080', color: '#fff' };
          return (
            <div key={doc.id} style={docCardStyle} data-testid={testIds.promptLab.docContextItem(doc.id)}>
              <span style={{ fontSize: 14 }}>📄</span>
              <span style={{ flex: 1 }}>{doc.title}</span>
              <span style={{ fontSize: 10, color: '#808080' }}>{doc.documentType}</span>
              <span
                style={{
                  ...badgeStyle,
                  background: confColors.bg,
                  color: confColors.color,
                }}
              >
                {doc.confidentialityLevel}
              </span>
              <button
                style={removeBtnStyle}
                onClick={() => handleRemove(doc.id)}
                data-testid={testIds.promptLab.docContextRemove(doc.id)}
              >
                X
              </button>
            </div>
          );
        })}

        {/* Drop zone for future drag-from-SharePoint */}
        <div style={dropZoneStyle} data-testid={testIds.promptLab.docContextDropZone}>
          Drop documents here from SharePoint
        </div>
      </div>
    </div>
  );
}
